// src/contexts/AuthContext.js

import React, { useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  browserLocalPersistence,
  setPersistence,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../services/firebase";
import i18n, { normalizeSBSLanguage } from "../i18n";

const AuthContext = React.createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function buildPendingUserProfile(user) {
  return {
    uid: user.uid,
    email: user.email || "",
    fullName: user.displayName || "",
    accessStatus: "pending",
    accessApproved: false,
    role: "user",
    preferredLanguage: normalizeSBSLanguage(i18n.resolvedLanguage || i18n.language),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState("");

  const signUp = async (email, password) => {
    await setPersistence(auth, browserLocalPersistence);
    return createUserWithEmailAndPassword(auth, email, password);
  };

  const signIn = async (email, password) => {
    await setPersistence(auth, browserLocalPersistence);
    return signInWithEmailAndPassword(auth, email, password);
  };

  const signOutUser = async () => {
    await signOut(auth);
    setCurrentUser(null);
    setUserData(null);
  };

  const refreshUserData = async () => {
    if (!auth.currentUser) {
      setUserData(null);
      return null;
    }

    try {
      const userRef = doc(db, "users", auth.currentUser.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const data = {
          uid: auth.currentUser.uid,
          ...userSnap.data(),
        };

        setUserData(data);
        applyPreferredLanguage(data);
        return data;
      }

      const pendingProfile = buildPendingUserProfile(auth.currentUser);
      await setDoc(userRef, pendingProfile, { merge: true });

      const localProfile = {
        uid: auth.currentUser.uid,
        email: auth.currentUser.email || "",
        fullName: auth.currentUser.displayName || "",
        accessStatus: "pending",
        accessApproved: false,
        role: "user",
        preferredLanguage: normalizeSBSLanguage(i18n.resolvedLanguage || i18n.language),
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      setUserData(localProfile);
      applyPreferredLanguage(localProfile);
      return localProfile;
    } catch (err) {
      console.error("Error refreshing user data:", err);
      setAuthError(err.message || "Unable to refresh user data.");
      setUserData(null);
      return null;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      setAuthError("");

      try {
        setCurrentUser(user);

        if (!user) {
          setUserData(null);
          return;
        }

        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const profile = {
            uid: user.uid,
            ...userSnap.data(),
          };
          setUserData(profile);
          applyPreferredLanguage(profile);
        } else {
          const pendingProfile = buildPendingUserProfile(user);

          await setDoc(userRef, pendingProfile, { merge: true });

          const localProfile = {
            uid: user.uid,
            email: user.email || "",
            fullName: user.displayName || "",
            accessStatus: "pending",
            accessApproved: false,
            role: "user",
            preferredLanguage: normalizeSBSLanguage(i18n.resolvedLanguage || i18n.language),
            createdAt: new Date(),
            updatedAt: new Date(),
          };
          setUserData(localProfile);
          applyPreferredLanguage(localProfile);
        }
      } catch (err) {
        console.error("AuthContext user data load error:", err);
        setAuthError(err.message || "Unable to load user profile.");
        setUserData(null);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userData,
    loading,
    authError,
    signUp,
    signIn,
    signOutUser,
    refreshUserData,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

function applyPreferredLanguage(profile) {
  if (!profile?.preferredLanguage) return;

  const preferredLanguage = normalizeSBSLanguage(profile.preferredLanguage);
  const activeLanguage = normalizeSBSLanguage(
    i18n.resolvedLanguage || i18n.language
  );

  if (activeLanguage !== preferredLanguage) {
    i18n.changeLanguage(preferredLanguage);
  }
}
